import React, { useState } from "react";
import axios from "axios";
import { Phone, MapPin, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import SectionHeading from "../components/site/SectionHeading";
import { PHONE_TEL, PHONE_DISPLAY, ADDRESS } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service_needed: "", project_details: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/leads`, form);
      setStatus({ state: "success", message: t.contact.success });
      setForm({ name: "", phone: "", email: "", service_needed: "", project_details: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail
        ? (typeof err.response.data.detail === "string" ? err.response.data.detail : t.contact.error)
        : t.contact.error;
      setStatus({ state: "error", message: msg });
    }
  };

  const serviceOptions = t.services.items.map((s) => s.title);

  return (
    <div data-testid="page-contact">
      <section className="pt-40 pb-16 border-b border-white/5">
        <div className="container-x">
          <SectionHeading overline={t.contact.overline} title={t.contact.title} subtitle={t.contact.subtitle} />
        </div>
      </section>

      <section className="section-y">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-4 space-y-6">
            <div className="hairline rounded-xl p-7 bg-[#141417]">
              <div className="text-overline">{t.common.phoneLabel}</div>
              <a href={`tel:${PHONE_TEL}`} className="mt-3 font-display text-3xl font-light text-white flex items-center gap-3 hover:text-[#0055FF]" data-testid="contact-phone">
                <Phone className="w-5 h-5 text-[#0055FF]" />
                {PHONE_DISPLAY}
              </a>
            </div>

            <div className="hairline rounded-xl p-7 bg-[#141417] space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-[#0055FF]" />
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/55">Studio</div>
                  <div className="mt-1 text-white">{ADDRESS}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-1 text-[#0055FF]" />
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-white/55">Hours</div>
                  <div className="mt-1 text-white">{t.common.openNow}</div>
                </div>
              </div>
            </div>

            <div className="hairline rounded-xl overflow-hidden">
              <iframe
                title="GA Signature Dallas, TX map"
                src="https://www.google.com/maps?q=4912+Haverwood+Ln,+Dallas,+TX+75287&output=embed"
                className="w-full h-[280px] grayscale"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          <div className="lg:col-span-8">
            <form
              onSubmit={onSubmit}
              className="hairline rounded-xl p-7 md:p-10 bg-[#141417] space-y-5"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label={t.contact.fields.name} htmlFor="name">
                  <input
                    id="name"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder={t.contact.placeholders.name}
                    className="input-dark"
                    data-testid="input-name"
                  />
                </Field>
                <Field label={t.contact.fields.phone} htmlFor="phone">
                  <input
                    id="phone"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder={t.contact.placeholders.phone}
                    className="input-dark"
                    data-testid="input-phone"
                  />
                </Field>
              </div>
              <Field label={t.contact.fields.email} htmlFor="email">
                <input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder={t.contact.placeholders.email}
                  className="input-dark"
                  data-testid="input-email"
                />
              </Field>
              <Field label={t.contact.fields.service} htmlFor="service">
                <select
                  id="service"
                  required
                  value={form.service_needed}
                  onChange={update("service_needed")}
                  className="input-dark"
                  data-testid="input-service"
                >
                  <option value="">—</option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
              <Field label={t.contact.fields.details} htmlFor="details">
                <textarea
                  id="details"
                  required
                  value={form.project_details}
                  onChange={update("project_details")}
                  placeholder={t.contact.placeholders.details}
                  rows={6}
                  className="input-dark resize-none"
                  data-testid="input-details"
                />
              </Field>

              {status.state === "success" && (
                <div className="flex items-start gap-3 rounded-md border border-[#00E676]/30 bg-[#00E676]/5 p-4 text-sm text-[#9EE9C0]" data-testid="form-success">
                  <CheckCircle2 className="w-4 h-4 mt-0.5" /> {status.message}
                </div>
              )}
              {status.state === "error" && (
                <div className="flex items-start gap-3 rounded-md border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300" data-testid="form-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5" /> {status.message}
                </div>
              )}

              <button
                type="submit"
                disabled={status.state === "loading"}
                className="btn-primary w-full sm:w-auto disabled:opacity-70 disabled:cursor-not-allowed"
                data-testid="submit-estimate-btn"
              >
                {status.state === "loading" ? t.contact.submitting : t.contact.submit}
              </button>
            </form>
          </div>
        </div>
      </section>

      <style>{`
        .input-dark {
          width: 100%;
          background: rgba(0,0,0,0.55);
          border: 1px solid rgba(255,255,255,0.12);
          color: #fff;
          padding: 0.8rem 1rem;
          border-radius: 0.5rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.95rem;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .input-dark::placeholder { color: rgba(255,255,255,0.35); }
        .input-dark:focus {
          outline: none;
          border-color: #0055FF;
          box-shadow: 0 0 0 3px rgba(0,85,255,0.15);
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export default Contact;
